import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";
import { BookingForm } from "@/components/BookingForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const ServicePage = ({ testid, overline, titleLines, image, intro, startingAt, steps, proofPoints, faqs, defaultService, tiers }) => {
  return (
    <div data-testid={testid}>
      {/* HERO */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={image} alt={overline} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-ink/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
          <p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-6">{overline}</p>
          <h1 className="font-serif text-white text-5xl md:text-8xl leading-[0.95] tracking-tight">
            <LineReveal lines={titleLines} delay={0.2} />
          </h1>
          <p className="text-golden-500 font-semibold mt-6 text-lg">{startingAt}</p>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <p className="font-serif text-3xl md:text-4xl text-sage-100 leading-snug">{intro}</p>
        </Reveal>
      </section>

      {/* STEPS / PROCESS */}
      {steps && (
        <section className="bg-forest-800/70 border-y border-forest-700 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal><h2 className="font-serif text-4xl md:text-5xl text-sage-100 mb-14">How it works</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((s, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="bg-forest-900 border border-forest-700 rounded-sm p-8 h-full transition-colors duration-300 hover:border-golden-500">
                    <span className="font-serif text-5xl text-golden-500/40">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-serif text-2xl text-sage-100 mt-4 mb-3">{s.t}</h3>
                    <p className="text-sage-300 leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TIERS */}
      {tiers && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-sage-100 mb-14">Ways to work together</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-forest-900 border border-forest-700 rounded-sm p-8 h-full flex flex-col transition-all duration-300 hover:border-golden-500 hover:-translate-y-1">
                  <h3 className="font-serif text-3xl text-sage-100 mb-1">{tier.name}</h3>
                  <p className="text-golden-500 font-semibold mb-6">{tier.price}</p>
                  <p className="text-sage-300 mb-6">{tier.who}</p>
                  <ul className="space-y-3 mt-auto">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex gap-3 text-sage-200 text-sm"><Check className="h-4 w-4 text-golden-500 shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* PROOF POINTS */}
      {proofPoints && (
        <section className="bg-forest-800/70 border-y border-forest-700 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
            {proofPoints.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border-l-2 border-golden-500 pl-6 py-2">
                  <p className="font-serif text-2xl text-sage-100 mb-2">{p.stat}</p>
                  <p className="text-sage-300">{p.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-sage-100 mb-10">Common questions</h2></Reveal>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-forest-700">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-sage-100 font-sans text-left text-lg hover:text-golden-500 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sage-300 text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* BOOKING CTA */}
      <section data-testid="service-booking" className="bg-forest-800/70 border-t border-forest-700 py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-4">Next step</p>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-100 mb-3">Book a discovery call.</h2>
            <p className="text-sage-300 mb-10">No pressure, no jargon. We'll figure out whether this is the right fit together.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-forest-900 border border-forest-700 rounded-sm p-8">
              <BookingForm defaultService={defaultService} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
